import { Field, FieldRenderProps } from '@progress/kendo-react-form';
import { NumericTextBox, NumericTextBoxProps } from '@progress/kendo-react-inputs';
import {
    FieldInput,
    FileUpload,
    DialogProps,
    DisputeDialog,
    DisputeEventResult,
    DisputeField,
    FieldWrapper,
} from 'components';
import { DisputeEventTypes } from 'enums';
import { useDisputeService } from 'services';
import { useAllResolutionReasons, useDisputeDetails } from 'state';
import { useFileUploadValidator } from 'hooks';
import styles from './CompleteDispute.module.scss';

/**
 * CompleteDispute component allows the user to close a dispute.
 * 
 * @param props - Additional properties passed to the Dialog
 * @returns Rendered component
 */
const CompleteDispute = (props: DialogProps) => {
    const { approvedAmount, resolutionReasonId } = useDisputeDetails();
    const disputeService = useDisputeService();
    const filesValidator = useFileUploadValidator(0);
    const reasons = useAllResolutionReasons();
    const eventType = reasons.find(reason => reason.resolutionReasonId === resolutionReasonId)?.eventType;
    const isApproved = eventType === DisputeEventTypes.DisputeApproved;

    const onSubmit = async (data: any) => {
        const { disputeNumber, files, reason, ...rest } = data;
        const fileData = files?.map((file: any) => file.getRawFile()) ?? [];

        const result = await disputeService?.resolveDispute(disputeNumber, {
            ...rest,
            eventTypeId: isApproved ? DisputeEventTypes.DisputePaid : DisputeEventTypes.DisputeCompleted,
            resolutionReasonId,
        }, fileData);
        return {
            error: !result && 'Error saving dispute'
        } as DisputeEventResult;
    };

    return (
        <DisputeDialog
            initialValues={{ approvedAmount, isPublic: false }}
            title={`Complete ${isApproved ? 'Approved' : 'Denied'} Dispute`}
            submitLabel="Complete"
            routeToNext
            onSubmit={onSubmit}
            {...props}
        >
            {isApproved &&
                <DisputeField
                    name="approvedAmount"
                    component={PaidAmount}
                    validator={amountValidator}
                    max={approvedAmount}
                />
            }
            <Field
                component={FieldInput}
                child={FileUpload}
                label="Please provide any additional documentation."
                name="files"
                validator={filesValidator}
            />
        </DisputeDialog>
    );
};

const PaidAmount = (props: FieldRenderProps) => (
    <FieldWrapper label="Amount Paid" required {...props}>
        <NumericTextBox
            id="paidAmount"
            aria-required="true"
            ariaLabelledBy="paidAmount_label"
            className={styles.input}
            min={0.01}
            format="c2"
            spinners={false}
            {...props as NumericTextBoxProps}
        />
    </FieldWrapper>
);

const amountValidator = (value: number) => value > 0 ? '' : 'Required. Please enter the amount paid';

export default CompleteDispute;